import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";

export default function StoreBuilder() {
  const [store, setStore] = useState(null);
  const [form, setForm] = useState({ name: "", slug: "", tagline: "", description: "", primary_color: "#0F2A5C", accent_color: "#F47A1F", banner_url: "", business_type: "dropshipping", contact_email: "" });

  useEffect(() => {
    api.get("/stores/mine").then((r) => {
      if (r.data) {
        setStore(r.data);
        setForm({
          name: r.data.name || "", slug: r.data.slug || "", tagline: r.data.tagline || "",
          description: r.data.description || "", primary_color: r.data.primary_color || "#0F2A5C",
          accent_color: r.data.accent_color || "#F47A1F", banner_url: r.data.banner_url || "",
          business_type: r.data.business_type || "dropshipping", contact_email: r.data.contact_email || "",
        });
      }
    });
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (store) {
        const { data } = await api.patch(`/stores/${store.id}`, form);
        setStore(data);
        toast.success("Store updated");
      } else {
        const { data } = await api.post("/stores", form);
        setStore(data);
        toast.success("Store launched!");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  return (
    <DashboardLayout title="Store Builder" subtitle="Design your white-label storefront. Changes go live instantly.">
      <div className="grid lg:grid-cols-3 gap-4">
        <form onSubmit={save} className="lg:col-span-2 v-card space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Store name" v={form.name} on={(v) => setForm({...form, name: v})} testid="store-name" required />
            <Field label="Slug" v={form.slug} on={(v) => setForm({...form, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "-")})} testid="store-slug" required disabled={!!store} />
          </div>
          <Field label="Tagline" v={form.tagline} on={(v) => setForm({...form, tagline: v})} testid="store-tagline" />
          <div>
            <label className="label-eyebrow mb-2 block">Description</label>
            <textarea rows={4} className="v-input" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="store-description" />
          </div>
          <Field label="Banner image URL" v={form.banner_url} on={(v) => setForm({...form, banner_url: v})} testid="store-banner" />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow mb-2 block">Primary color</label>
              <div className="flex gap-2">
                <input type="color" value={form.primary_color} onChange={(e) => setForm({...form, primary_color: e.target.value})} className="w-12 h-10 rounded-sm border border-border" data-testid="store-primary-color" />
                <input className="v-input flex-1 font-mono" value={form.primary_color} onChange={(e) => setForm({...form, primary_color: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="label-eyebrow mb-2 block">Accent color</label>
              <div className="flex gap-2">
                <input type="color" value={form.accent_color} onChange={(e) => setForm({...form, accent_color: e.target.value})} className="w-12 h-10 rounded-sm border border-border" data-testid="store-accent-color" />
                <input className="v-input flex-1 font-mono" value={form.accent_color} onChange={(e) => setForm({...form, accent_color: e.target.value})} />
              </div>
            </div>
          </div>
          <Field label="Contact email" type="email" v={form.contact_email} on={(v) => setForm({...form, contact_email: v})} testid="store-contact-email" />
          <button className="v-btn-primary mt-2" data-testid="store-save">{store ? "Update store" : "Launch store"}</button>
        </form>

        <aside className="v-card h-fit">
          <div className="label-eyebrow text-secondary mb-2">Preview</div>
          <div className="rounded-sm overflow-hidden border border-border">
            <div className="h-32 bg-cover bg-center" style={{ backgroundImage: `url(${form.banner_url})`, backgroundColor: form.primary_color }} />
            <div className="p-4" style={{ backgroundColor: form.primary_color, color: "white" }}>
              <div className="font-mono text-[10px] opacity-70">vendra.uk/store/{form.slug || "your-slug"}</div>
              <h3 className="font-display font-black text-xl mt-1">{form.name || "Your Store"}</h3>
              <p className="text-sm opacity-80 mt-1">{form.tagline || "Your tagline"}</p>
              <button className="mt-3 text-xs font-semibold px-3 py-1 rounded-sm" style={{ backgroundColor: form.accent_color, color: "white" }}>Shop now</button>
            </div>
          </div>
          {store && (
            <Link to={`/store/${store.slug}`} target="_blank" className="v-btn-outline w-full mt-4" data-testid="store-view-live">
              View live store <ExternalLink size={14} />
            </Link>
          )}
        </aside>
      </div>
    </DashboardLayout>
  );
}

function Field({ label, type = "text", v, on, testid, required, disabled }) {
  return (
    <div>
      <label className="label-eyebrow mb-2 block">{label}</label>
      <input type={type} className="v-input" value={v} onChange={(e) => on(e.target.value)} required={required} disabled={disabled} data-testid={testid} />
    </div>
  );
}
